import type { DispatchOptions, NexusSignalDefinition, SubscriptionOptions } from "./event.types.js";
import * as symbols from "../utils/symbols.js";
import { assertNamespaceType, storeFlags } from "../utils/utils.js";
import type { Listener, TargetStore } from "../store/store.types.js";
import type { AnyFunction } from "../utils/utils.types.js";
import type { Unset } from "../proxy/proxy.types.js";

/**
 *
 *
 *
 *
 */
export function isSignalDefinition(value: unknown): value is NexusSignalDefinition<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.prototype.hasOwnProperty.call(value, symbols.event)
  );
}

export function notififyStartIfDefined(store: TargetStore, shouldProcess: boolean): boolean {
  let hasNotified = false;

  if (!store.start || !shouldProcess) return hasNotified;

  store.stop =
    store.start(
      (value) => {
        hasNotified = true;
        dispatchEvent(store, [value, { retain: true }]);
      },
      (updater) => {
        hasNotified = true;
        dispatchEvent(store, [updater(store.detail), { retain: true }]);
      },
    ) ?? undefined;

  return hasNotified;
}

export const notifyStopIfDefined = (store: TargetStore): void => {
  if (!store.stop) return;
  if (store.listeners.length > 0) return;

  store.stop();
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export function invokeListener(
  this: unknown,
  detail: unknown,
  prevous: unknown,
  [listener, options, controller]: Listener,
): unknown | Error {
  /*                                                          */
  if (options.onlyChanges && prevous === detail) return undefined;

  /*                                          */
  /*                                     */
  /*                                                                          */
  try {
    return listener.call(this, detail, prevous);
  } catch (e) {
    return e;
  } finally {
    /*                                                                         */
    if (options.once) controller.abort();
  }
}

const cleanupCallback = function (this: TargetStore, entry: Listener): void {
  this.listeners.splice(this.listeners.indexOf(entry), 1);
};

const getController = (): [AbortController, AbortSignal, () => void] => {
  const controller = new AbortController();
  return [controller, controller.signal, controller.abort.bind(controller)] as const;
};

export function addListener(
  store: TargetStore,
  listener: AnyFunction,
  options: SubscriptionOptions = {},
): Unset {
  if (typeof listener !== "function")
    throw Error(`Invalid listener type ${typeof listener} provided. a function is expected`);

  assertNamespaceType(store, storeFlags[1], "add listener to");

  /*                                                                               */
  /*                                                                            */
  notififyStartIfDefined(store, store.listeners.length === 0);

  /*                                                                                       */
  const [mainController, mainSignal, mainAbort] = getController();

  /*                                             */
  const listenerEntry: Listener = [listener, options, mainController];
  store.listeners.push(listenerEntry);

  /*                                                      */
  /*                                                       */
  mainSignal.addEventListener("abort", cleanupCallback.bind(store, listenerEntry), {
    once: true,
  });

  /*                                                                       */
  /*                                                             */
  /*                                            */
  if (store.retained && !options.skipRetained)
    invokeListener(store.detail, undefined, listenerEntry);

  /*                                                                */
  /*                              */
  if (options.signal)
    options.signal.addEventListener("abort", mainAbort, {
      once: true,
      signal: mainSignal, /*                                                  */
    });

  /*                                 */
  return () => {
    const isDeleted = store.listeners.indexOf(listenerEntry) > -1;
    mainAbort();
    notifyStopIfDefined(store);
    return isDeleted;
  };
}

export function deleteRetentions(store: TargetStore): void {
  delete store.detail;
  delete store.retained;
}

export function dispatchEvent(
  store: TargetStore,
  parameters: [
    optionsOrDetail?: DispatchOptions | undefined | unknown,
    options?: DispatchOptions | undefined,
  ],
): (unknown | Error)[] {
  assertNamespaceType(store, storeFlags[1], "emit an event on");

  let detail: unknown = parameters[0];
  let options = parameters[1] as DispatchOptions | undefined;

  if (!parameters[1] && detail && typeof detail === "object" && "retain" in detail) {
    options = detail as DispatchOptions;
    detail = undefined;
  } else {
    options ||= {};
  }

  const previous: unknown | undefined = options.retain ? store.detail : undefined;

  if (store.retention) {
    clearTimeout(store.retention);
    deleteRetentions(store);
  }

  if (options.retain) {
    store.retained = true;
    store.detail = detail;
  }

  /*                              */
  if (typeof options.retain === "number" && options.retain > 0 && options.retain < Infinity) {
    store.retention = setTimeout(() => deleteRetentions(store), options.retain);
  }

  /*                             */
  const results = [...store.listeners].map(invokeListener.bind(null, detail, previous));
  const retval = [...results];

  /*                      */
  Object.defineProperties(retval, {
    then: {
      value: (async (a) => await a?.(results)) as Promise<unknown>["then"],
      enumerable: false,
    },
    catch: {
      value: (async () => {}) as Promise<unknown>["catch"],
      enumerable: false,
    },
    finally: {
      value: (async (a) => a?.()) as Promise<unknown>["finally"],
      enumerable: false,
    },
  });

  return retval;
}
